import { KeyValueObj } from "@/common/types";
import { formatToNaira } from "@/common/utils/general";
import styled from "styled-components";

const highColor = "#7661E2";
const lowColor = "#C52419";
const mediumColor = "#59E6F6";

function getColor(status: string): string {
  if (status === "high") return highColor;
  if (status === "medium") return mediumColor;
  return lowColor;
}

const ChartWrapper = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.bg.prim_500};
  box-shadow: ${({ theme }) => theme.shadow.shad_100};
  padding: 3rem 1.2rem 1.5rem;

  .chart-sub {
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 150%;
  }

  .lower-sub {
    margin-top: 1rem;
  }

  .lower-sec {
    margin-top: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .product-text {
      font-size: 0.75rem;
      color: ${({ theme }) => theme.text.prim_100};
      font-weight: 400;
      width: 6rem;
    }

    .lines {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      width: 100%;
    }

    .line-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .quantity {
      flex: 1;
      height: 1.5rem;
      display: flex;
      justify-content: space-between;
      gap: 0.35rem;

      p {
        font-size: 0.75rem;
        color: ${({ theme }) => theme.text.prim_100};
        font-weight: 500;
      }
    }

    .legend {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: center;
      gap: 0.5rem;
      padding-left: 1.5rem;
      max-width: 15%;

      .legend-item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.2rem;
      }

      .box {
        width: 1rem;
        height: 1rem;
        border-radius: 0.1rem;
      }

      .medium {
        background-color: ${mediumColor};
      }

      .low {
        background-color: ${lowColor};
      }

      .high {
        background-color: ${highColor};
      }
    }
  }
`;

interface IStrengthLine {
  percent: number;
  status: string;
}
const StrengthLine = styled.div<IStrengthLine>`
  background-color: green;
  height: 100%;
  width: ${({ percent }) => percent + "%"};
  background-color: ${({ status }) => getColor(status)};
`;

interface IInventory {
  product_brand: string;
  status: string;
  total_quantity_in_stock: number;
  reorder_level: number;
}

function getScale(maxValue: number) {
  const unit = maxValue / 10;
  const roundedToTen = Math.ceil(unit / 10) * 10;
  let scale: number[] = [];
  for (let i = 0; i <= 10; i++) {
    scale.push(i * roundedToTen);
  }

  return scale;
}

export interface ILineChartProps {
  data: KeyValueObj;
}

function getStatus(inventory: IInventory): string {
  const stock = inventory?.total_quantity_in_stock;
  const reOrderValue = inventory?.reorder_level;

  console.log({ stock, reOrderValue });

  if (stock > reOrderValue * 2) return "high";
  if (stock > reOrderValue && stock < reOrderValue * 2) return "medium";
  return "low";
}

export function LineChart({ data }: ILineChartProps) {
  let maxValue = 100;
  data?.inventory_items?.forEach?.((datum: KeyValueObj) => {
    maxValue = Math.max(maxValue, datum?.total_quantity_in_stock);
  });

  const scale = getScale(maxValue);
  const lastScaleValue = scale?.[scale.length - 1];

  return (
    <ChartWrapper>
      <h3 className="chart-sub">
        Total Inventory Value: {formatToNaira(data?.total_inventory_value)}
      </h3>
      <h3 className="chart-sub lower-sub">Stock Levels</h3>

      <section className="lower-sec">
        <div className="lines">
          {data?.inventory_items?.map((inventory: IInventory, idx: number) => (
            <div key={idx} className="line-wrapper">
              <h3 className="product-text ">
                {inventory.product_brand ?? "N/A"}
              </h3>
              <div className="quantity">
                <StrengthLine
                  percent={Math.floor(
                    (inventory?.total_quantity_in_stock / lastScaleValue) * 100
                  )}
                  status={getStatus(inventory)}
                />
              </div>
            </div>
          ))}

          <div className="line-wrapper">
            <h3 className="product-text ">{""}</h3>
            <div className="quantity">
              <div className="quantity">
                {scale.map((sc: number, idx: number) => (
                  <p key={idx}>{sc}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="legend">
          <div className="legend-item">
            <p className="product-text">Critically Low</p>
            <div className="box low"></div>
          </div>
          <div className="legend-item">
            <p className="product-text">Low</p>
            <div className="box medium"></div>
          </div>
          <div className="legend-item">
            <p className="product-text">Normal</p>
            <div className="box high"></div>
          </div>
        </div>
      </section>
    </ChartWrapper>
  );
}
